import { IconMinus, IconPlus } from "@/lib/icons";
import styles from "./booking.module.css";
import { Button, Flex, App, Tag } from "antd";
import BorderLogo from "../../images/tykkit_border_text.png";
import { useMemo, useState, useEffect } from "react";
import { formatCurrency } from "@/lib/formatCurrency";
import RegistrationLimitMessage from "../RegistrationLimitMessage";
import { TICKET_THEME, getTicketTheme } from "./ticketTheme";
import { getEligibleSeasonPasses } from "@/lib/seasonPassHelpers";
import SeasonPassTicketCard from "./SeasonPassTicketCard";
import { applySeasonPassSelection } from "./SeasonPassStep";
import { trackSelectTicket, trackTicketQuantityUpdated } from "@/lib/analytics";
import { trackAddToCart } from "@/lib/metaPixel";

export { TICKET_THEME, getTicketTheme };

export function Ticket({
  id,
  name,
  price,
  currency,
  min_per_transaction,
  max_per_transaction,
  qty,
  setActiveStep,
  setSelectedID,
  selectedID,
  orderDetails,
  setOrderDetails,
  event_id,
  event_name,
  status,
  quantity,
  requires_approval,
  available,
  available_quantity,
  is_sold_out,
  setQuantity,
  description,
  showAvailableTickets = false,
  isFinished,
  isUniqueRegistration = false,
  index = 0,
  theme: customTheme,
  children,
}) {
  const { message } = App.useApp();
  const theme = customTheme || getTicketTheme(index, id);
  const isSelected = selectedID === id;
  const [disabled, setDisabled] = useState(false);
  const effectiveMin = isUniqueRegistration ? 1 : min_per_transaction;
  const effectiveMax = isUniqueRegistration ? 1 : max_per_transaction;

  const resolvedAvailable = (() => {
    if (is_sold_out === true || is_sold_out === 1 || is_sold_out === "1") {
      return 0;
    }
    const raw = available ?? available_quantity;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  })();
  const isSoldOut =
    resolvedAvailable <= 0 ||
    resolvedAvailable < Number(effectiveMin || 1);

  const handleDecrease = () => {
    const newQty = Math.max(effectiveMin, quantity - 1);
    setQuantity(newQty);
    setDisabled(false);
    setOrderDetails((prev) => ({
      ...prev,
      ticket: {
        id,
        qty: newQty,
        price,
        name,
        event_id,
        event_name,
        description,
        currency,
        requires_approval,
        min_per_transaction,
        max_per_transaction,
        index,
      },
    }));
    trackTicketQuantityUpdated({
      event_id,
      event_name,
      ticket_id: id,
      ticket_name: name,
      quantity: newQty,
      price,
      currency,
    });
  };

  const handleIncrease = () => {
    if (isSoldOut || resolvedAvailable <= quantity) {
      setDisabled(true);
      message.warning(
        isSoldOut
          ? "This ticket is sold out"
          : `Only ${resolvedAvailable} ticket(s) available`,
      );
      return;
    }
    if (quantity >= effectiveMax) {
      setDisabled(true);
      message.warning(
        isUniqueRegistration
          ? "Only 1 ticket can be booked per mobile number for this event"
          : `Max ${effectiveMax} tickets allowed`,
      );
      return;
    }
    const newQty = quantity + 1;
    if (newQty >= effectiveMax) {
      message.warning(
        isUniqueRegistration
          ? "Only 1 ticket can be booked per mobile number for this event"
          : `Max ${effectiveMax} tickets allowed`,
      );
    } else if (newQty >= resolvedAvailable) {
      message.warning(`Only ${resolvedAvailable} ticket(s) available`);
    }
    setQuantity(newQty);
    setOrderDetails((prev) => ({
      ...prev,
      ticket: {
        id,
        qty: newQty,
        price,
        description,
        name,
        event_id,
        event_name,
        currency,
        requires_approval,
        min_per_transaction,
        max_per_transaction,
        index,
      },
    }));
    trackTicketQuantityUpdated({
      event_id,
      event_name,
      ticket_id: id,
      ticket_name: name,
      quantity: newQty,
      price,
      currency,
    });
  };

  const handleAdd = () => {
    if (isSoldOut) {
      message.warning("This ticket is sold out");
      return;
    }
    setSelectedID(id);
    setQuantity(effectiveMin);
    setDisabled(false);
    if (effectiveMin >= effectiveMax) {
      message.warning(
        isUniqueRegistration
          ? "Only 1 ticket can be booked per mobile number for this event"
          : `Max ${effectiveMax} tickets allowed`,
      );
    }
    trackSelectTicket({
      event_id,
      event_name,
      ticket_id: id,
      ticket_name: name,
      price,
      quantity: effectiveMin,
      currency,
    });
    setOrderDetails((prev) => ({
      ...prev,
      ticket: {
        id,
        qty: effectiveMin,
        price,
        description,
        name,
        event_id,
        event_name,
        currency,
        requires_approval,
        min_per_transaction,
        max_per_transaction,
        index,
      },
    }));

    trackAddToCart({
      eventId: event_id,
      eventName: event_name,
      ticketId: id,
      ticketName: name,
      quantity: effectiveMin,
      price: price,
      currency: currency || "INR",
    });
  };

  return (
    <div className={styles.ticket}>
      <div className={styles.border} style={{ backgroundColor: theme.bg }}>
        <img src={BorderLogo.src} alt="tykkit" />
      </div>

      <div className={styles.body}>
        <Flex vertical gap={8}>
          <div className={styles.title}>{name}</div>
          {description ? (
            <div
              className="text-gray-500 overflow-hidden break-words"
              dangerouslySetInnerHTML={{
                __html:
                  description.length > 100
                    ? description.substring(0, 100) + "..."
                    : description,
              }}
            ></div>
          ) : (
            <></>
          )}
          <div className="flex flex-col">
            <div className={styles.pricing}>
              {formatCurrency(price, currency)}
            </div>
            <span className="text-red-600 text-xs font-medium mt-0.5">
              {showAvailableTickets &&
              !isSoldOut &&
              resolvedAvailable <= max_per_transaction &&
              status !== "draft" &&
              status !== "closed" &&
              resolvedAvailable > 0 &&
              resolvedAvailable > Number(min_per_transaction || 0)
                ? `only ${resolvedAvailable} tickets  left`
                : ""}
            </span>
            {isSelected && quantity >= effectiveMax && (
              <span className="text-xs text-red-600 font-medium mt-0.5">
                {isUniqueRegistration
                  ? "Max 1 ticket allowed"
                  : `Max ${effectiveMax} tickets allowed`}
              </span>
            )}
            {isSelected &&
              quantity >= resolvedAvailable &&
              quantity < effectiveMax &&
              !isSoldOut && (
              <span className="text-xs text-red-600 font-medium mt-0.5">
                Only {resolvedAvailable} tickets available
              </span>
            )}
          </div>
          {children}
        </Flex>

        {isFinished ? (
          <Tag
            color="blue"
            className="!px-3 !py-2 !mx-0"
            style={{ borderWidth: 1, borderStyle: "solid", borderColor: "#91caff" }}
          >
            Completed
          </Tag>
        ) : status === "draft" ? (
          <Tag
            color="green"
            className="!px-3 !py-2 !mx-0"
            style={{ borderWidth: 1, borderStyle: "solid", borderColor: "#b7eb8f" }}
          >
            Coming Soon
          </Tag>
        ) : status === "closed" ? (
          <Tag
            color="blue"
            className="!px-3 !py-2 !mx-0"
            style={{ borderWidth: 1, borderStyle: "solid", borderColor: "#91caff" }}
          >
            Sale Closed
          </Tag>
        ) : isSoldOut ? (
          <Tag
            color="red"
            className="!px-3 !py-2 !mx-0"
            style={{ borderWidth: 1, borderStyle: "solid", borderColor: "#ffccc7" }}
          >
            Sold Out
          </Tag>
        ) : (
          setActiveStep &&
          (isSelected ? (
            isUniqueRegistration ? (
              <Tag color="success" className="!px-3 !py-2 !mx-0">
                Added
              </Tag>
            ) : (
              <div className={styles.quantityButton}>
                <Button
                  icon={<IconMinus size={16} />}
                  style={{ lineHeight: 1 }}
                  onClick={handleDecrease}
                  disabled={quantity <= effectiveMin}
                />
                <span>{quantity}</span>
                <Button
                  icon={<IconPlus size={16} />}
                  style={{ lineHeight: 1 }}
                  disabled={
                    disabled ||
                    quantity >= effectiveMax ||
                    quantity >= resolvedAvailable
                  }
                  onClick={handleIncrease}
                />
              </div>
            )
          ) : (
            <Button onClick={handleAdd}>Add</Button>
          ))
        )}

        {qty && <span className="text-[32px] font-[500]">x{qty}</span>}
      </div>
    </div>
  );
}

export default function TicketStep({
  data,
  setActiveStep,
  orderDetails,
  setOrderDetails,
  tickets = [],
  setTickets,
  type,
  customize,
  showAvailableTickets = false,
  isFinished,
  alreadyRegistered = false,
  isUniqueRegistration = false,
  isTicketTransferable,
  order_id = null,
}) {
  const [quantity, setQuantity] = useState(orderDetails?.ticket?.qty || 0);
  const [selectedID, setSelectedID] = useState(orderDetails?.ticket?.id);

  useEffect(() => {
    setSelectedID(orderDetails?.ticket?.id);
    setQuantity(orderDetails?.ticket?.qty || 0);
  }, [orderDetails?.ticket?.id, orderDetails?.ticket?.qty]);

  useEffect(() => {
    if (!isUniqueRegistration) return;
    if (quantity > 1) {
      setQuantity(1);
      setOrderDetails((prev) =>
        prev?.ticket ? { ...prev, ticket: { ...prev.ticket, qty: 1 } } : prev,
      );
    }
  }, [isUniqueRegistration, quantity, setOrderDetails]);

  // Filter tickets by selected zone if applicable
  const filteredTickets = useMemo(() => {
    if (!orderDetails?.zone || tickets.length === 0) return tickets;

    if (orderDetails.zone.ticket_id) {
      return tickets.filter((t) => t.id === orderDetails.zone.ticket_id);
    }

    const zoneLabel = (
      orderDetails.zone.zone_lable ||
      orderDetails.zone.name ||
      ""
    ).toLowerCase();

    return tickets.filter((t) => {
      const ticketName = (t.name || "").toLowerCase();
      const ticketCategory = (t.category || "").toLowerCase();
      return (
        ticketName.includes(zoneLabel) ||
        ticketCategory.includes(zoneLabel) ||
        zoneLabel.includes(ticketName)
      );
    });
  }, [tickets, orderDetails?.zone]);

  const displayTickets = useMemo(() => {
    return filteredTickets.length > 0 ? filteredTickets : tickets;
  }, [filteredTickets, tickets]);

  const dayTickets = useMemo(
    () => (displayTickets || []).filter((t) => !t?.is_season_pass),
    [displayTickets],
  );

  // Same enriched season passes as the Season Passes step (with Valid for list).
  const seasonPasses = useMemo(() => {
    if (type === "season") {
      return getEligibleSeasonPasses(data, {
        childEventId: orderDetails?.event_id,
        date: orderDetails?.date,
      });
    }
    if (customize) return [];
    if (!orderDetails?.event_id && !orderDetails?.date) {
      // Still show season rows from current ticket list if any.
      return (displayTickets || [])
        .filter((t) => t?.is_season_pass)
        .map((t) => ({
          ...t,
          coveredEvents: t.coveredEvents || [],
          eventCount: t.eventCount || 0,
          validityStart: t.validityStart || null,
          validityEnd: t.validityEnd || null,
        }));
    }
    return getEligibleSeasonPasses(data, {
      childEventId: orderDetails?.event_id,
      date: orderDetails?.date,
    });
  }, [
    data,
    type,
    customize,
    displayTickets,
    orderDetails?.event_id,
    orderDetails?.date,
  ]);

  const handleSelectSeasonPass = (pass, index) => {
    applySeasonPassSelection({
      pass,
      index,
      orderDetails,
      setOrderDetails,
      setTickets,
      // Stay on individual path until Next → review (keeps Tickets step in flow).
      options: { bookingPath: "individual", replaceTickets: false },
    });
    setSelectedID(pass.id);
    setQuantity(Number(pass?.min_per_transaction || 1));
  };

  const handleSeasonPassQuantity = (pass, nextQty) => {
    setQuantity(nextQty);
    setOrderDetails((prev) => {
      if (!prev?.ticket || prev.ticket.id !== pass.id) return prev;
      return {
        ...prev,
        ticket: { ...prev.ticket, qty: nextQty },
      };
    });
  };

  if (alreadyRegistered && isUniqueRegistration) {
    return (
      <div className={styles.ticketSelection}>
        <RegistrationLimitMessage order_id={order_id} />
      </div>
    );
  }
  const messageClass =
    isUniqueRegistration && !isTicketTransferable
      ? "text-orange-600"
      : "text-blue-600";

  const wrapDayTicketOrderDetails = (updater) => {
    setOrderDetails((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return {
        ...next,
        booking_path: "individual",
        is_season_pass: false,
        season_pass_step: false,
        ticket: next?.ticket
          ? { ...next.ticket, is_season_pass: false }
          : next?.ticket,
      };
    });
  };

  const renderDayTicket = (ticketProps, index = 0) => (
    <Ticket
      key={ticketProps.id}
      index={index}
      {...ticketProps}
      orderDetails={orderDetails}
      setOrderDetails={wrapDayTicketOrderDetails}
      setActiveStep={setActiveStep}
      selectedID={selectedID}
      setSelectedID={setSelectedID}
      quantity={selectedID === ticketProps.id ? quantity : 0}
      setQuantity={setQuantity}
      showAvailableTickets={showAvailableTickets}
      isFinished={isFinished}
      isUniqueRegistration={isUniqueRegistration}
      isTicketTransferable={isTicketTransferable}
    />
  );

  return (
    <div className={styles.ticketSelection}>
      {(isUniqueRegistration || !isTicketTransferable) && (
        <p className={`text-sm mb-4 ${messageClass}`}>
          {isUniqueRegistration && !isTicketTransferable
            ? "Only one ticket can be booked per mobile number for this event and tickets are also non-transferable."
            : isUniqueRegistration
              ? "Only one ticket can be booked per mobile number for this event."
              : "Tickets are non-transferable."}
        </p>
      )}
      <Flex gap={42} vertical align="stretch" style={{ width: "100%" }}>
        {/* UPPER: day / individual tickets */}
        {type !== "season" && !customize && dayTickets.length > 0 && (
          <div>
            <h3 className={styles.sectionTitle2}>Select tickets</h3>
            <Flex vertical gap={16}>
              {dayTickets.map((t, index) =>
                renderDayTicket(
                  {
                    id: t.id,
                    name: t.name,
                    price: t.price,
                    currency: t.currency,
                    max_per_transaction: t.max_per_transaction,
                    min_per_transaction: t.min_per_transaction,
                    event_id: t.event_id,
                    event_name: data?.name,
                    status: t.status,
                    available: t.available,
                    description: t.description,
                    requires_approval: t.requires_approval,
                    is_sold_out: t.is_sold_out,
                    available_quantity: t.available_quantity,
                  },
                  index,
                ),
              )}
            </Flex>
          </div>
        )}

        {/* LOWER (or only section for type=season): season passes */}
        {(type === "season" || !customize) && seasonPasses.length > 0 && (
          <div>
            <h3 className={styles.sectionTitle2}>
              Select Your Multi-Day & Multi-City Season Pass
            </h3>
            <Flex vertical gap={16}>
              {seasonPasses.map((pass, index) => (
                <SeasonPassTicketCard
                  key={pass.id}
                  pass={pass}
                  index={dayTickets.length + index}
                  selected={selectedID === pass.id}
                  quantity={selectedID === pass.id ? quantity : 0}
                  onSelect={handleSelectSeasonPass}
                  onQuantityChange={handleSeasonPassQuantity}
                  highlightChildEventId={orderDetails?.event_id}
                  highlightDate={orderDetails?.date}
                  highlightTime={orderDetails?.time}
                  isUniqueRegistration={isUniqueRegistration}
                />
              ))}
            </Flex>
          </div>
        )}
      </Flex>
    </div>
  );
}
